<script>

import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import {onMounted, ref} from "vue";
import axios from "axios";
import useTools from "@/use/Tools";
import {useBankStore} from "@/stores/Bank";
import useSharedMethod from "@/use/SharedMethod";

export default {
    setup() {
        const {mxRules} = useValidation();
        const {$route, $refs} = useTools();
        const {getBack} = useSharedMethod();
        const {dialog, loading} = useSharedData();
        
        const bankStore = useBankStore();
        
        const bank = ref({fieldsMapper: []});
        const companyMappers = ref([{globel: true}]);
        const salaryMappers = ref([{globel: false}]);
        
        const companyFields = [
            {id: "companyBanque", name: "Code Banque"},
            {id: "companyAgency", name: "Code Agence"},
            {id: "companyAccountKey", name: "Compte Societé avec Clé"},
            {id: "companyAccount", name: "Compte Societé sans Clé"},
            {id: "companyKey", name: "Societé Clé"},
            {id: "companyName", name: "Raison Social"},
            {id: "virmentDate", name: "Date de l'ordre de Virement "},
            {id: "virmentNumber", name: "Nombre de Virements"},
            {id: "totalPrice", name: "Montant Total des Virements"},
            {id: "companyLabel", name: "Libellé"},
        ];
        const salaryFields = [
            {id: "bank", name: "Code Banque"},
            {id: "agency", name: "Code Agence"},
            {id: "accountKey", name: "N°Compte avec Clé"},
            {id: "account", name: "N°Compte sans Clé"},
            {id: "key", name: "Clé"},
            {id: "firstName", name: "Nom"},
            {id: "lastName", name: "Prénom"},
            {id: "fullName", name: "Nom et Prénom"},
            {id: "address", name: "Adresse"},
            {id: "price", name: "Montant"},
        ];
        
        function findById(id) {
            loading.value = true;
            axios.get(`/banks/${id}`).then((response) => {
                bank.value = response.data;
                companyMappers.value = bank.value.fieldsMapper.filter(e => e.globel);
                companyMappers.value.sort((a, b) => a.cellIndex - b.cellIndex);
                
                salaryMappers.value = bank.value.fieldsMapper.filter(e => !e.globel);
                salaryMappers.value.sort((a, b) => a.cellIndex - b.cellIndex);
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                bank.value.fieldsMapper = salaryMappers.value.concat(companyMappers.value);
                axios.put('/banks', bank.value).then((response) => {
                    bankStore.updateBank(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        onMounted(() => findById($route.params.id));
        
        return {
            mxRules,
            dialog,
            loading,
            companyFields,
            salaryFields,
            companyMappers,
            salaryMappers,
            bank,
            update,
            getBack
        }
    }
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="800"
        scrollable
    >
        <v-card>
            <w-toolbar color="orange" :title="$t('bank.xlsxmapper')"/>
            <v-card-text>
                <v-form ref="form" lazy-validation>
                    <v-container fluid>
                        <v-row>
                            <v-col cols="6">
                                <w-label :label="$t('bank.company_record_line')" required/>
                                <w-text-field
                                    v-model="bank.companyStartRow"
                                    type="number"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="6">
                                <w-label :label="$t('bank.worker_record_line')" required/>
                                <w-text-field
                                    v-model="bank.salaryStartRow"
                                    type="number"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                        </v-row>
                        <br/>
                        <w-fieldset
                            :title="$t('bank.company_information')"
                            show-add-action
                            @add="companyMappers.push({ globel: true })"
                        >
                            <v-row
                                dense
                                v-for="(mapper, index) in companyMappers"
                                :key="`company-${index}`"
                            >
                                <v-col cols="6">
                                    <w-label :label="$t('common.field')" required/>
                                    <w-enum-select
                                        v-model="mapper.field"
                                        :items="companyFields"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="5">
                                    <w-label :label="$t('bank.index_column')" required/>
                                    <w-text-field
                                        v-model="mapper.cellIndex"
                                        type="number"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="1">
                                    <w-btn-icon
                                        icon="mdi-trash-can"
                                        color="red"
                                        classe="mt-6"
                                        @press="companyMappers.splice(index, 1)"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset
                            :title="$t('bank.worker_information')"
                            show-add-action
                            @add="salaryMappers.push({ globel: false })"
                        >
                            <v-row
                                dense
                                v-for="(mapper, index) in salaryMappers"
                                :key="`salary-${index}`"
                            >
                                <v-col cols="6">
                                    <w-label :label="$t('common.field')" required/>
                                    <w-enum-select
                                        v-model="mapper.field"
                                        :items="salaryFields"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="5">
                                    <w-label :label="$t('bank.index_column')" required/>
                                    <w-text-field
                                        v-model="mapper.cellIndex"
                                        type="number"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="1">
                                    <w-btn-icon
                                        icon="mdi-trash-can"
                                        color="red"
                                        classe="mt-6"
                                        @press="salaryMappers.splice(index, 1)"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-update
                    show
                    :loading="loading"
                    @press="update"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>